import { z } from 'zod'
import { isoDateTimeSchema } from '@customTypes/cloudmix/api'

export const JOB_STATUSES = {
  scheduling: 'scheduling',
  scheduled: 'scheduled',
  running: 'running',
  completed: 'completed',
  failed: 'failed',
  cancelled: 'cancelled',
} as const

export const jobStatusSchema = z.nativeEnum(JOB_STATUSES)
export type JobStatus = z.infer<typeof jobStatusSchema>

export const JOB_STATUS_COLORS: Record<JobStatus, string> = {
  scheduling: 'gray',
  scheduled: 'blue',
  running: 'blue',
  completed: 'green',
  failed: 'red',
  cancelled: 'gray',
}

export const JOB_STATUS_LABELS: Record<JobStatus, string> = {
  scheduling: 'Scheduling',
  scheduled: 'Scheduled',
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
}

// Ordered lists of all statuses (avoids Object.values calls at call sites)
export const JOB_STATUS_LIST: readonly JobStatus[] = [
  JOB_STATUSES.scheduling,
  JOB_STATUSES.scheduled,
  JOB_STATUSES.running,
  JOB_STATUSES.completed,
  JOB_STATUSES.failed,
  JOB_STATUSES.cancelled,
]

export const jobSchema = z.object({
  jobId: z.string().uuid(),
  jobDefinitionName: z.string(),
  jobDefinitionVersion: z.number().int().positive(),
  configuration: z.record(z.unknown()),
  callbackUrl: z.string().url().nullable(),
  callbackReference: z.string().nullable(),
  status: jobStatusSchema,
  progressPercent: z.number().min(0).max(100).nullable(),
  createdAt: isoDateTimeSchema,
  startedAt: isoDateTimeSchema.nullable(),
  lastHeartbeatAt: isoDateTimeSchema.nullable(),
  endedAt: isoDateTimeSchema.nullable(),
})
export type Job = z.infer<typeof jobSchema>

export const listJobsRequestSchema = z
  .object({
    status: z.array(jobStatusSchema).optional(),
    limit: z.number().int().min(1).max(100).optional(),
    cursor: z.string().min(1).optional(),
  })
  .refine(
    ({ cursor, status, limit }) =>
      cursor === undefined || (status === undefined && limit === undefined),
    { message: 'cursor must be provided by itself' }
  )
export type ListJobsRequest = z.infer<typeof listJobsRequestSchema>

export const listJobsResponseSchema = z.object({
  jobs: z.array(jobSchema),
  nextCursor: z.string().nullable(),
})
export type ListJobsResponse = z.infer<typeof listJobsResponseSchema>

export const jobsRouteSearchSchema = z.object({
  status: z.array(jobStatusSchema).optional(),
})
